import { defineStore } from "pinia";
import date from 'quasar/src/utils/date/date.js';;
import * as _ from "lodash";

export const usePortalStore = defineStore({
	id: "portal",
	state: () => ({
		originalPayloadData: null,
		payload: null,
		isAppReady: false,
		shouldOpenDialog: false,
		dialogTitle: "",
		dialogMsg: "",
		visitorFieldsForm: null as any,
		disableHomeButtonClick: false,
		currentPageName: null as any,
		errorDescription: "portal_errorDescription" as any,
		photoData: null as any,
		disableNext: false as Boolean,
		profileName: "",

		// payloads:
		visitDetails: null as any,
		photoDetails: null as any,
		photoValidationEnabled: null as any,
		fields: null as any,
		inductions: null as any,
		idParam: null as any,
		referrer: {
			name: null as any,
			visitorSelfEntry: false as Boolean
		},
		tenantId: "",
		visitCodeQr: { enabled: true } as any,
		cdfRefMap: null as any,

		// navigation
		next: () => void 0 as void,
		dataPolicyAgreed: null,
		dataPolicyRequired: true,
		currentNavIndex: 1, //start at landing page
		isValid: true,

		// navPageConfig
		navPageConfig: [] as any,
		_quasar: null as any,
		_translate: null as any,
		
		//events:
		eventNames: {
		ndaDeclinedSkipped: "Portal - NDA Declined/Skipped",
		detailsUpdateFailed: "Portal - Details Update Failed",
		detailsUpdated: "Portal - Details Updated",
		addCompetencyFailed: "Portal - Add Competency Failed"
		}
	}),
	getters: {
		getFormattedDate: (state) => {
			return (dateVal: string) => date.formatDate(dateVal, "DD-MMM-YYYY h:mm A");
		},
		getGroups: (state) => {
			return (fields: any) => _.chain(fields).map("groupName").uniq().value();
		},
		getConfirmGroups: (state) => {
			return (fields: any) => _.chain(_.filter(fields, fld => { return fld.showOnConfirm; })).map("groupName").uniq().value();
		},
		getTotalPages: (state) => {
			return () => state.navPageConfig.length - 2 - state.navPageConfig.filter((x: any) => x.isAction === true).length;
		},
		getSelectOptions: (state) => {
			return (options: any[]) => {
				const listOptions: any[] = [];
				let key: string;
				_.forEach(options, (option) => {
					key = option.value.replace(/ /g, '-').toLowerCase();
					listOptions.push({ label: state._translate(key), value: option.value });
				});
				return listOptions;
			};
		},
		getButtonText: (state) => {
			return (isLeft: boolean) => {
				if (
					state.navPageConfig.length &&
					state.navPageConfig[state.currentNavIndex].buttons
				) {
					return isLeft
						? state.navPageConfig[state.currentNavIndex].buttons[0].name
						: state.navPageConfig[state.currentNavIndex].buttons[1].name;
				}
				return isLeft ? "portal_back" : "portal_continue";
			};
		},
		getInduction: (state) => {
			return (pageName: string) => {
				return state.inductions.find((ind: any) => ind.pageName === pageName)
			}
		},
		getInductionsLength: (state) => {
			return (isCompleted: any) => {
				return _.filter(state.inductions, (ind) => {
					return ind.isCompleted === isCompleted;
				}).length;
			};
		},
		getFieldValue: (state) => {
			return (field: any) => {
				if (field.originalValue === "" || field.originalValue === null || !field.originalValue.length) return "-";
				// boolean type
				else if (field.typeId === 6) {
					return field.originalValue === "on" ? state._translate("portal_checkboxTrue") : state._translate("portal_checkboxFalse");
				} 
				// dropdown type
				else if (field.typeId === 2 && (field.subTypeId === 5 || field.subTypeId === 4)) {
					return state._translate(field.originalValue.replace(/ /g, '-').toLowerCase());
				}	
				// multiselect type
				else if (field.typeId === 2 && field.subTypeId === 7) {
					const displayValue = _.map(field.originalValue, (fld) => state._translate(fld.replace(/ /g, '-').toLowerCase()));
					return displayValue.join(', ');
				}				
				return field.originalValue;
			
			};
		},
		getNavIndexFromPageName: (state) => {
			return (name: string) => {
				return _.findIndex(state.navPageConfig, (page: any) => {
					return page.name === name;
				});
			};
		},
		shouldHideBackButton: (state) => {
			return () => {
				if (state.navPageConfig) {
					const msgPagesNames: string[] = [
						"errorPage",
						"completePage",
						"completeStatusPage",
					];
					msgPagesNames.push(state.navPageConfig[1].name) // [0] is always error page
					return _.includes(
						msgPagesNames,
						state.navPageConfig[state.currentNavIndex].name
					);
				}
			};
		},
		shouldHideNextButton: (state) => {
			return () => {
				if (state.navPageConfig) {
					const msgPagesNames: string[] = [
						"errorPage",
						"completePage",
						"completeStatusPage",
					];
					return _.includes(
						msgPagesNames,
						state.navPageConfig[state.currentNavIndex].name
					);
				}
			};
		},
		shouldHideSkipButton: (state) => {
			return () => {
				if (state.navPageConfig && state.navPageConfig.length > 1) {
					const showButton = state.navPageConfig[state.currentNavIndex].component.name === "InductionPage" &&
						state.navPageConfig[state.currentNavIndex].skippable === true;
					return !showButton;
				}
				return true;
			};
		},
		getApiPath: (state) => {
			return () => {
				let apiPath: string = process.env.VUE_APP_BASEURL;
				if (!apiPath) apiPath = '/';
				if (!apiPath.startsWith("/")) apiPath = `/${apiPath}`;
				if (!apiPath.endsWith("/")) apiPath = `${apiPath}/`;
				return apiPath
			};
		}
	},
	actions: {
		allCompetenciesCompleted(competencyId: number) : boolean {
			// Get inductions matching the competency id
			const matches = _.filter(this.inductions, ind => { return ind.competencyId === competencyId; });
			// check if any matching inductions are not completed
			if (matches)
				return !_.some(matches, ind => { return !ind.isCompleted; });
			return false;
		},
		validate() {
			let hasInvalidField = false;
			const validateFields = new Promise((resolve, reject) => {
				_.forEach(this.visitorFieldsForm, (input, index: number, array) => {
					input.validate().then((success: any) => {
						if (!success) {
							hasInvalidField = true;
						}
						if (index === array.length - 1) {
							resolve(hasInvalidField);
						}
					});
				});
			});

			validateFields.then((hasInvalidField) => {
				if (hasInvalidField) {
					this.displayDialog(
						"portal_visitorFieldsValidationTitle",
						"portal_visitorFieldsValidationMsg"
					);
				} else {
					this.isValid = true;
				}
			});
		},
		displayDialog(title: string, msg: string) {
			this.dialogTitle = title;
			this.dialogMsg = msg;
			this.shouldOpenDialog = true;
			this.isValid = false;
		}
	},
});
